import * as Yup from "yup";
import {
  Plan,
  PlanType,
  RenewalOptions,
  RenewalStrategy,
  RenewalType
} from "../models";
import { EmployerModuleType } from "../models/accounts/employerModuleType";

interface AllowancesFormValues {
  name: string;
  type: PlanType;
  tiers: { id?: string; name: string; amount: number }[];
  renewalOptions: RenewalOptions;
  serviceSectorIds: string[];
}

interface GiftingFormValues {
  name: string;
  tiers: { id?: string; name: string; amount: number }[];
  renewalOptions: RenewalOptions;
}

interface RewardsRecognitionFormValues {
  name: string;
  type: PlanType;
  tiers: { id?: string; name: string; amount: number }[];
  renewalOptions: RenewalOptions;
  controls: {
    payments: {
      isEnabled: boolean;
      reasonCategories: string[];
    };
  };
}

interface PublicTransportFormValues {
  name: string;
}

type ModulePlanInitialValuesMap = {
  [EmployerModuleType.ALLOWANCES]: AllowancesFormValues;
  [EmployerModuleType.GIFTING]: GiftingFormValues;
  [EmployerModuleType.REWARDS_RECOGNITION]: RewardsRecognitionFormValues;
  [EmployerModuleType.PUBLIC_TRANSPORT]: PublicTransportFormValues;
};

export const useModulePlan = <T extends EmployerModuleType>(
  moduleType: T,
  plan?: Plan
): {
  initialValues: ModulePlanInitialValuesMap[T];
  validationSchema: Yup.ObjectSchema<any>;
} => {
  const getInitialValues = (moduleType: EmployerModuleType) => {
    switch (moduleType) {
      case EmployerModuleType.ALLOWANCES:
        return {
          name: plan?.name ?? "",
          type: plan?.type ?? PlanType.ONGOING,
          tiers: plan?.tiers // Omitting createdAt for existing tiers
            ? plan.tiers.map((t) => ({
                id: t.id,
                name: t.name,
                amount: t.amount
              }))
            : [],
          renewalOptions: plan?.renewalOptions ?? {
            type: RenewalType.INDIVIDUAL_START_DATE,
            strategy: RenewalStrategy.RESET,
            periodInMonths: 12
          },
          serviceSectorIds:
            plan?.controls.serviceSectors?.map((sector) => sector.id) ?? []
        };
      case EmployerModuleType.GIFTING:
        return {
          name: plan?.name ?? "",
          tiers: plan?.tiers // Omitting createdAt for existing tiers
            ? plan.tiers.map((t) => ({
                id: t.id,
                name: t.name,
                amount: t.amount
              }))
            : [],
          renewalOptions: plan?.renewalOptions ?? {
            type: RenewalType.INDIVIDUAL_START_DATE,
            strategy: RenewalStrategy.EXPIRE,
            periodInMonths: 12
          }
        };
      case EmployerModuleType.REWARDS_RECOGNITION:
        return {
          name: plan?.name ?? "",
          type: plan?.type ?? PlanType.ONGOING,
          tiers: plan?.tiers // Omitting createdAt for existing tiers
            ? plan.tiers.map((t) => ({
                id: t.id,
                name: t.name,
                amount: t.amount
              }))
            : [],
          renewalOptions: plan?.renewalOptions ?? {
            type: RenewalType.INDIVIDUAL_START_DATE,
            strategy:
              plan?.type === PlanType.ONE_OFF
                ? RenewalStrategy.EXPIRE
                : RenewalStrategy.RESET,
            periodInMonths: 12,
            startDate: null
          },
          controls: {
            payments: {
              isEnabled: plan?.controls?.payments?.isEnabled ?? true,
              reasonCategories: plan?.controls?.payments?.reasonCategories ?? []
            }
          }
        };
      case EmployerModuleType.PUBLIC_TRANSPORT:
        return {
          name: plan?.name ?? ""
        };
    }
  };

  const getValidationSchema = (moduleType: EmployerModuleType) => {
    const baseSchema = Yup.object({
      name: Yup.string().required("Name is required")
    });

    const schemaWithTiers = baseSchema.shape({
      tiers: Yup.array()
        .of(
          Yup.object().shape({
            name: Yup.string().required("Tier name is required"),
            amount: Yup.object().shape({
              amount: Yup.number()
                .required("Amount is required")
                .moreThan(0, "Amount must be greater than 0")
            })
          })
        )
        .min(1, "At least one tier is required")
    });

    switch (moduleType) {
      case EmployerModuleType.ALLOWANCES:
        return schemaWithTiers.shape({
          serviceSectorIds: Yup.array().min(
            1,
            "At least one category must be selected"
          )
        });
      case EmployerModuleType.GIFTING:
        return schemaWithTiers;
      case EmployerModuleType.REWARDS_RECOGNITION:
        return schemaWithTiers.shape({
          controls: Yup.object().shape({
            payments: Yup.object().shape({
              reasonCategories: Yup.array().min(
                1,
                "At least one reason must be provided"
              )
            })
          })
        });
      case EmployerModuleType.PUBLIC_TRANSPORT:
        return baseSchema;
    }
  };

  return {
    initialValues: getInitialValues(
      moduleType
    ) as ModulePlanInitialValuesMap[T],
    validationSchema: getValidationSchema(moduleType)
  };
};
